/**
 * LA CADENA DE VENTAS A UTILIDAD NETA, Y LA DEL DINERO QUE ENTRÓ.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  SE LEE COMO UNA RESTA, Y POR ESO VIVE EN SU PROPIO ARCHIVO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *     Ventas − Costo = Utilidad bruta − Gastos = Utilidad neta
 *
 * Puestas en ese orden se entiende de dónde sale la última sin que nadie lo
 * explique. Desordenadas son cinco números sueltos. Los signos «−» y «=» van
 * dibujados entre las tarjetas: son parte del dato, no adorno.
 *
 * ⚠️ Está aquí y no dentro de una pantalla porque la usan DOS: el reporte del
 * período y Utilidad y pérdida. Copiarla habría dejado dos versiones de la misma
 * resta, y el día que cambie una regla —qué cuenta como gasto, qué no— una de
 * las dos pantallas se quedaría atrás sin que nada avisara.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ «VENTAS» E «INGRESOS» SON DOS PREGUNTAS (2026-10-21)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * La primera tarjeta se llamaba «Ingresos» y era lo VENDIDO, crédito incluido.
 * El socio del dueño lo vio en un negocio que vende casi todo a crédito: arriba
 * «Ingresos $5.710,91 · $5.612,87 a crédito», y abajo, en Formas de cobro, más
 * de $18.000 entrando por Binance. Las dos cifras eran ciertas, pero ninguna
 * pantalla decía cuánto dinero entró ni por qué no se parecía a lo vendido. Y
 * en Inicio «Ingresos» ya era el dinero que entró.
 *
 * Ahora la resta empieza en «Ventas», y el reporte lleva debajo su segunda
 * cadena: Cobrado al vender + Abonos de crédito = Ingresos. La misma cuenta que
 * Inicio, y el total de Formas de cobro (sin el vuelto).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ EN EL CELULAR, UNA CUENTA EN COLUMNA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Las cinco tarjetas iban en una fila también en el celular («INGRE…», «$5.…»):
 * con `flex-1` y `flex-wrap` nunca bajaban de línea. Ahora, donde no caben, las
 * cifras van una debajo de la otra con su signo delante, como se escribe una
 * resta en papel. Se mide el hueco (`@container`), no la pantalla: con el menú
 * abierto, una ventana de 1080 tampoco tiene sitio para cinco tarjetas.
 */

import { Fragment } from "react";
import { Equal, Minus, Plus } from "lucide-react";
import { cx } from "@/components/ui";
import { comoFormato, formatMoney, toNumber } from "@/lib/money";
import { plural } from "@/lib/texto";

type Tono = "exito" | "peligro";

/** Un paso de la cadena: su signo delante (salvo el primero) y su cifra. */
type Paso = {
  signo?: "menos" | "igual" | "mas";
  rotulo: string;
  valor: string;
  pie?: string;
  tono?: Tono;
  destacada?: boolean;
};

// =============================================================================
//  LAS CIFRAS
// =============================================================================

export function CifrasEnCadena({
  resumen,
  formato,
  conDinero = false,
  onVerCobros,
}: {
  resumen: import("@/lib/types").ResumenOperativo;
  formato: ReturnType<typeof comoFormato>;
  /** La segunda cadena, la del dinero. Solo en el reporte del período. */
  conDinero?: boolean;
  /** Abre la pestaña «Formas de cobro». */
  onVerCobros?: () => void;
}) {
  const conMargen = resumen.utilidad_neta !== null;
  const dinero = (v: string) => formatMoney(v, formato);

  const resultado: Paso[] = [
    {
      rotulo: "Ventas",
      valor: dinero(resumen.ingresos),
      pie:
        toNumber(resumen.fiado) > 0
          ? `Incluye ${dinero(resumen.fiado)} a crédito`
          : plural(resumen.ventas, "venta", "ventas"),
    },
    ...(conMargen
      ? ([
          {
            signo: "menos",
            rotulo: "Costo de lo vendido",
            valor: dinero(resumen.costo_de_lo_vendido!),
            tono: "peligro",
          },
          { signo: "igual", rotulo: "Utilidad bruta", valor: dinero(resumen.utilidad_bruta!) },
          {
            signo: "menos",
            rotulo: "Gastos",
            valor: dinero(resumen.gastos!),
            tono: "peligro",
            pie:
              [
                toNumber(resumen.mercancia_pagada ?? "0") > 0
                  ? `${dinero(resumen.mercancia_pagada!)} de mercancía, ya va en el costo`
                  : null,
                toNumber(resumen.retiros ?? "0") > 0
                  ? `${dinero(resumen.retiros!)} de retiros, aparte`
                  : null,
              ]
                .filter(Boolean)
                .join(" · ") || undefined,
          },
          {
            signo: "igual",
            rotulo: "Utilidad neta",
            valor: dinero(resumen.utilidad_neta!),
            tono: toNumber(resumen.utilidad_neta!) >= 0 ? "exito" : "peligro",
            destacada: true,
          },
        ] satisfies Paso[])
      : []),
  ];

  const entro: Paso[] = [
    {
      rotulo: "Cobrado al vender",
      valor: dinero(resumen.cobrado_al_vender),
      // Es lo que separa esta cifra del total de la tabla de Formas de cobro,
      // que cuenta el vuelto como egreso.
      pie: toNumber(resumen.vuelto) > 0 ? `Sin contar ${dinero(resumen.vuelto)} de vuelto` : undefined,
    },
    {
      signo: "mas",
      rotulo: "Abonos de crédito",
      valor: dinero(resumen.abonos),
      pie:
        resumen.abonos_cantidad > 0
          ? plural(resumen.abonos_cantidad, "abono", "abonos")
          : undefined,
    },
    { signo: "igual", rotulo: "Ingresos", valor: dinero(resumen.entro), destacada: true },
  ];

  const nota = (
    <p className="text-xs text-muted">
      Lo vendido a crédito suma en Ventas el día de la venta, y en Ingresos el día en que se cobra.
      {onVerCobros && (
        <>
          {" "}
          <button
            type="button"
            onClick={onVerCobros}
            className="font-semibold text-accent underline underline-offset-2"
          >
            Ver por forma de cobro
          </button>
        </>
      )}
    </p>
  );

  return (
    <div className="@container">
      {/* ═══════════ Ancho: las tarjetas en fila, con sus signos ═══════════ */}
      <div className="hidden space-y-2 @5xl:block">
        <FilaDeTarjetas pasos={resultado} />
        {conDinero && <FilaDeTarjetas pasos={entro} junto={nota} />}
      </div>

      {/* ═══════════ Estrecho: cada cadena en columna ═══════════ */}
      <div className={cx("grid items-start gap-3 @5xl:hidden", conDinero && "@3xl:grid-cols-2")}>
        <EnColumna titulo="Resultado del período" pasos={resultado} />
        {conDinero && <EnColumna titulo="Dinero que entró" pasos={entro} pie={nota} />}
      </div>

      {resumen.anuladas > 0 && (
        <p className="mt-2 text-xs text-muted">
          {resumen.anuladas} {resumen.anuladas === 1 ? "venta anulada" : "ventas anuladas"} en
          el período, sin contar en estas cifras.
        </p>
      )}
    </div>
  );
}

/**
 * Las tarjetas de una cadena, en las mismas cinco columnas siempre: así las de
 * la segunda fila caen debajo de las de la primera, y lo que sobra a la derecha
 * es para `junto`.
 */
function FilaDeTarjetas({ pasos, junto }: { pasos: Paso[]; junto?: React.ReactNode }) {
  const huecos = (5 - pasos.length) * 2;
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)_auto_minmax(0,1fr)_auto_minmax(0,1fr)_auto_minmax(0,1fr)] items-stretch gap-y-2">
      {pasos.map((p) => (
        <Fragment key={p.rotulo}>
          {p.signo && <Signo icono={iconoDe(p.signo)} />}
          <Cifra {...p} />
        </Fragment>
      ))}
      {junto && huecos > 0 && (
        <div className="flex items-center pl-4" style={{ gridColumn: `span ${huecos} / span ${huecos}` }}>
          {junto}
        </div>
      )}
    </div>
  );
}

/** La misma cadena escrita como una resta en papel: una cifra por renglón. */
function EnColumna({
  titulo,
  pasos,
  pie,
}: {
  titulo: string;
  pasos: Paso[];
  pie?: React.ReactNode;
}) {
  return (
    <div className="rounded-[var(--radius-card)] border border-line bg-surface p-4 shadow-[var(--shadow-card)]">
      <p className="rotulo">{titulo}</p>
      <dl className="mt-2">
        {pasos.map((p) => (
          <div
            key={p.rotulo}
            className={cx(
              "flex items-baseline justify-between gap-3 border-t border-line py-2 first:border-t-0",
              p.destacada && "-mx-2 mt-1 rounded-[var(--radius-control)] border-t-0 bg-accent-soft px-2",
            )}
          >
            <dt className="min-w-0">
              <span className={cx("text-sm text-ink", p.destacada && "font-semibold")}>
                <span className="inline-block w-4 text-faint" aria-hidden>
                  {p.signo === "menos" ? "−" : p.signo === "igual" ? "=" : p.signo === "mas" ? "+" : ""}
                </span>
                {p.rotulo}
              </span>
              {p.pie && <span className="block pl-4 text-[11px] text-muted">{p.pie}</span>}
            </dt>
            <dd
              className={cx(
                "tabular shrink-0 font-semibold",
                p.destacada ? "text-base" : "text-sm",
                colorDe(p.tono),
              )}
            >
              {p.valor}
            </dd>
          </div>
        ))}
      </dl>
      {pie && <div className="mt-3 border-t border-line pt-3">{pie}</div>}
    </div>
  );
}

function iconoDe(signo: NonNullable<Paso["signo"]>) {
  if (signo === "menos") return <Minus size={14} />;
  if (signo === "mas") return <Plus size={14} />;
  return <Equal size={14} />;
}

function colorDe(tono?: Tono) {
  return tono === "exito" ? "text-success" : tono === "peligro" ? "text-danger" : "text-ink";
}

// También las usa el IVA del período (`Impuestos.tsx`): la misma cadena, con «=» y «+».
export function Signo({ icono }: { icono: React.ReactNode }) {
  return (
    <div className="hidden shrink-0 items-center px-0.5 text-faint sm:flex" aria-hidden>
      {icono}
    </div>
  );
}

export function Cifra({
  rotulo,
  valor,
  pie,
  tono,
  destacada,
}: {
  rotulo: string;
  valor: string;
  pie?: string;
  tono?: Tono;
  destacada?: boolean;
}) {
  return (
    <div
      className={cx(
        "min-w-0 flex-1 rounded-[var(--radius-card)] border p-3 shadow-[var(--shadow-card)]",
        destacada ? "border-accent/40 bg-accent-soft" : "border-line bg-surface",
      )}
    >
      <p className="rotulo truncate">{rotulo}</p>
      <p className={cx("tabular mt-1 truncate text-lg font-semibold", colorDe(tono))}>{valor}</p>
      {/* Hasta dos renglones: «Incluye $5.612,87 a crédito» cortado a la mitad no dice nada. */}
      {pie && <p className="mt-0.5 line-clamp-2 text-[11px] text-muted">{pie}</p>}
    </div>
  );
}
